'use client';

import { useCallback, useRef, useState } from 'react';
import Link from 'next/link';

import { Turnstile, type TurnstileRef } from '@/components/turnstile/Turnstile';
import { fromFetchError } from '@/lib/api/error';
import { useCreateContactRequestMutation } from '@/services/contactApi';
import {
  Button,
  Form,
  FormInput,
  FormSelect,
  FormTextarea,
  useToast,
  useZodForm,
} from '@holistic/components';
import { ArrowRight } from '@holistic/components/icons';

import { MESSAGE_MAX_LENGTH, sendMailSchema } from './send-mail-schema';

const topics = [
  { label: 'Tamir talebi', value: 'Tamir talebi' },
  { label: 'Mevcut talebim hakkında', value: 'Mevcut talebim hakkında' },
  { label: 'Garanti', value: 'Garanti' },
  { label: 'Kargo ve teslimat', value: 'Kargo ve teslimat' },
  { label: 'Kurumsal iş birliği', value: 'Kurumsal iş birliği' },
  { label: 'Diğer', value: 'Diğer' },
];

const SendMessage = () => {
  const toast = useToast();
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const turnstileRef = useRef<TurnstileRef>(null);

  const [createContactRequest, { isLoading }] =
    useCreateContactRequestMutation();

  const form = useZodForm(sendMailSchema, {
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      topic: '',
      message: '',
      kvkk: false,
    },
  });

  const message = form.watch('message') ?? '';

  const handleTurnstileSuccess = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileError = useCallback(() => {
    setTurnstileToken('');
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken('');
  }, []);

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      await createContactRequest({
        name: values.name,
        email: values.email,
        phone: values.phone,
        subject: values.topic,
        message: values.message,
        turnstileToken: turnstileToken || undefined,
      }).unwrap();

      toast.success('Mesajınız alındı. En kısa sürede dönüş yapacağız.');
      form.reset();
      setTurnstileToken('');
      turnstileRef.current?.reset();
    } catch (error) {
      toast.error(
        fromFetchError(error, 'Mesajınız gönderilemedi, tekrar deneyin.')
          .message
      );
      turnstileRef.current?.reset();
    }
  });

  return (
    <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-xs md:p-8">
      <div className="space-y-1">
        <h2 className="text-2xl font-semibold tracking-wide text-black">
          Mesaj Bırakın
        </h2>
        <p className="text-sm text-gray-500">
          Ekibimiz en kısa sürede size dönüş yapacaktır.
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormInput
              name="name"
              label="Ad Soyad"
              placeholder="Adınız ve soyadınız"
              required
            />
            <FormInput
              name="phone"
              label="Telefon"
              placeholder="(5xx) xxx xx xx"
              maskPrefix="+90 "
              mask="(999) 999 99 99"
              required
            />
            <FormInput
              name="email"
              label="E-posta"
              placeholder="E-posta adresinizi yazınız"
              required
            />
            <FormSelect
              name="topic"
              label="Konu"
              placeholder="Konu seçiniz"
              options={topics}
              required
            />
          </div>

          <div>
            <FormTextarea
              name="message"
              label="Mesajınız"
              placeholder="Nasıl yardımcı olabiliriz?"
              maxLength={MESSAGE_MAX_LENGTH}
              required
            />
            <p className="mt-1 text-right text-xs text-gray-400">
              {message.length}/{MESSAGE_MAX_LENGTH}
            </p>
          </div>

          <label className="flex cursor-pointer items-start gap-2.5 text-sm leading-relaxed text-gray-600">
            <input
              type="checkbox"
              {...form.register('kvkk')}
              className="accent-primary mt-0.5 size-4 shrink-0 cursor-pointer"
            />
            <span>
              Kişisel verilerimin,{' '}
              {/* Yeni sekmede açılır ki doldurulan form kaybolmasın. */}
              <Link
                href="/sozlesmeler/aydinlatma-metni"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary font-medium hover:underline"
              >
                KVKK Aydınlatma Metni
              </Link>{' '}
              kapsamında işlenmesini kabul ediyorum.
            </span>
          </label>
          {form.formState.errors.kvkk && (
            <p className="text-destructive text-sm">
              {form.formState.errors.kvkk.message}
            </p>
          )}

          <Turnstile
            ref={turnstileRef}
            onSuccess={handleTurnstileSuccess}
            onError={handleTurnstileError}
            onExpire={handleTurnstileExpire}
            className="py-1"
          />

          <Button
            type="submit"
            size="lg"
            disabled={isLoading}
            className="w-full rounded-full px-8 font-semibold md:w-fit"
          >
            {isLoading ? 'Gönderiliyor…' : 'Gönder'}
            <ArrowRight />
          </Button>
        </form>
      </Form>
    </section>
  );
};

export default SendMessage;
